/**
 * Step 2: Taken Beoordelen
 *
 * Manager scores employee tasks with weighting.
 */

import { useState, useEffect, useMemo } from 'react';
import { useEvaluation, useSaveTaskScores } from '../useEvaluations';
import { useEmployeeAssignedTasks } from '../../employees/useEmployeeTasks';
import { useWizard } from './WizardContext';
import { ScoreInput, ScoreDisplay } from './ScoreInput';
import { AgreementTypeSelector, isAgreementTypeRequired, type AgreementWithDescription } from './AgreementTypeSelector';
import type { TaskScore } from '../../../lib/evaluationsApi';
import { evaluationsApi } from '../../../lib/evaluationsApi';

interface Step2TasksProps {
  evaluationId: number;
}

interface LocalTaskScore {
  taskId: number;
  score: number | null;
  toelichting: string;
  selectedAgreementTypes: number[];
  agreements: AgreementWithDescription[];
}

export function Step2Tasks({ evaluationId }: Step2TasksProps) {
  const { data: evaluation, isLoading: isLoadingEval } = useEvaluation(evaluationId);
  const { data: assignedTasksData, isLoading: isLoadingTasks } = useEmployeeAssignedTasks(
    evaluation?.employeeId || 0
  );
  const saveTaskScores = useSaveTaskScores();
  const { stepData, updateStepData, prevStep, nextStep } = useWizard();

  const [localScores, setLocalScores] = useState<LocalTaskScore[]>([]);

  // Initialize local scores from wizard state or evaluation data
  useEffect(() => {
    if (!assignedTasksData?.tasks) return;

    const existingScores = stepData.step2?.taskScores || evaluation?.taskScores || [];
    const existingAgreements = stepData.step2?.taskAgreements || {};
    const initialScores: LocalTaskScore[] = assignedTasksData.tasks.map((task) => {
      const existing = existingScores.find((s) => s.taskId === task.id);
      const taskAgreements = existingAgreements[task.id] || [];
      return {
        taskId: task.id,
        score: existing?.score ?? null,
        toelichting: existing?.toelichting || '',
        selectedAgreementTypes: existing?.selectedAgreementTypes || [],
        agreements: taskAgreements,
      };
    });
    setLocalScores(initialScores);
  }, [assignedTasksData, evaluation, stepData.step2?.taskScores, stepData.step2?.taskAgreements]);

  // Calculate points per task and earned points
  // Total task weight is 50 points (50% of 100)
  const TASK_WEIGHT = 50; // TODO: Get from settings

  const taskPointsInfo = useMemo(() => {
    if (!assignedTasksData?.tasks || localScores.length === 0) {
      return { pointsPerTask: 0, totalEarned: 0, maxPoints: TASK_WEIGHT };
    }

    const taskCount = assignedTasksData.tasks.length;
    const pointsPerTask = taskCount > 0 ? TASK_WEIGHT / taskCount : 0;
    let totalEarned = 0;

    localScores.forEach((ls) => {
      if (ls.score !== null) {
        totalEarned += (ls.score / 5) * pointsPerTask;
      }
    });

    return {
      pointsPerTask: Math.round(pointsPerTask * 100) / 100,
      totalEarned: Math.round(totalEarned * 100) / 100,
      maxPoints: TASK_WEIGHT,
    };
  }, [localScores, assignedTasksData]);

  const handleScoreChange = (taskId: number, score: number) => {
    setLocalScores((prev) =>
      prev.map((ls) => {
        if (ls.taskId === taskId) {
          // Clear agreement types and agreements if score is >= 3
          const newAgreementTypes = score >= 3 ? [] : ls.selectedAgreementTypes;
          const newAgreements = score >= 3 ? [] : ls.agreements;
          return { ...ls, score, selectedAgreementTypes: newAgreementTypes, agreements: newAgreements };
        }
        return ls;
      })
    );
  };

  const handleToelichtingChange = (taskId: number, toelichting: string) => {
    setLocalScores((prev) =>
      prev.map((ls) => (ls.taskId === taskId ? { ...ls, toelichting } : ls))
    );
  };

  const handleAgreementTypesChange = (taskId: number, types: number[]) => {
    setLocalScores((prev) =>
      prev.map((ls) => (ls.taskId === taskId ? { ...ls, selectedAgreementTypes: types } : ls))
    );
  };

  const handleAgreementsChange = (taskId: number, agreements: AgreementWithDescription[]) => {
    setLocalScores((prev) =>
      prev.map((ls) => (ls.taskId === taskId ? { ...ls, agreements } : ls))
    );
  };

  const handleSave = async () => {
    const validScores: TaskScore[] = localScores
      .filter((ls) => ls.score !== null)
      .map((ls) => ({
        taskId: ls.taskId,
        score: ls.score!,
        toelichting: ls.toelichting || undefined,
        selectedAgreementTypes: ls.selectedAgreementTypes.length > 0 ? ls.selectedAgreementTypes : undefined,
      }));

    // Collect agreements per task for wizard state
    const taskAgreements: Record<number, AgreementWithDescription[]> = {};
    localScores.forEach((ls) => {
      if (ls.agreements.length > 0) {
        taskAgreements[ls.taskId] = ls.agreements;
      }
    });

    // Update wizard state with scores, agreements AND total count
    updateStepData('step2', {
      taskScores: validScores,
      taskAgreements,
      totalTasks: localScores.length,
    });

    // Save scores to server
    if (validScores.length > 0) {
      await saveTaskScores.mutateAsync({ evaluationId, scores: validScores });
    }

    // Save agreements to server (for each task with agreements)
    for (const ls of localScores) {
      if (ls.agreements.length > 0 && ls.score !== null && ls.score <= 2) {
        // Find the task score ID (we need it to link agreements)
        // For now, create agreements linked to the evaluation
        for (const agreement of ls.agreements) {
          if (agreement.beschrijving.trim()) {
            try {
              await evaluationsApi.createScoreAgreement(evaluationId, {
                agreementTypeId: agreement.agreementTypeId,
                beschrijving: agreement.beschrijving,
                // Note: taskScoreId would be set by backend based on taskId
              });
            } catch {
              // Ignore errors for now - agreements might already exist
            }
          }
        }
      }
    }
  };

  // Check if all required agreement types are filled
  const hasInvalidAgreementTypes = localScores.some(
    (ls) => ls.score !== null && ls.score <= 2 && ls.selectedAgreementTypes.length === 0
  );

  const handleNext = async () => {
    await handleSave();
    nextStep();
  };

  const isLoading = isLoadingEval || isLoadingTasks;
  const completedCount = localScores.filter((ls) => ls.score !== null).length;
  const totalCount = localScores.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Taken laden...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Stap 2: Taken Beoordelen</h2>
        <p className="mt-1 text-sm text-gray-500">
          Beoordeel elke taak op een schaal van 1-5. De eindscore wordt gewogen berekend.
        </p>
      </div>

      {/* Progress & Average */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Voortgang</span>
            <span className="text-sm text-gray-500">
              {completedCount} van {totalCount} taken
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <span className="text-sm font-medium text-gray-700 block mb-2">
            Takenscore ({taskPointsInfo.maxPoints} punten beschikbaar)
          </span>
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-gray-900">
              {taskPointsInfo.totalEarned.toFixed(1)}
            </span>
            <span className="text-lg text-gray-500">/ {taskPointsInfo.maxPoints}</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {taskPointsInfo.pointsPerTask.toFixed(1)} punten per taak
          </p>
        </div>
      </div>

      {/* Task List */}
      <div className="space-y-4">
        {assignedTasksData?.tasks?.map((task) => {
          const localScore = localScores.find((ls) => ls.taskId === task.id);
          const selfEval = evaluation?.selfEvaluations?.find((se) => se.taskId === task.id);

          return (
            <div
              key={task.id}
              className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
            >
              {/* Task header */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{task.name}</h3>
                    {task.beschrijving && (
                      <p className="mt-1 text-sm text-gray-500">{task.beschrijving}</p>
                    )}
                  </div>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                    max {taskPointsInfo.pointsPerTask.toFixed(1)} punten
                  </span>
                </div>
              </div>

              <div className="px-6 py-4 space-y-4">
                {/* Self-evaluation reference */}
                {selfEval && (
                  <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                    <span className="text-sm text-yellow-700">Zelf-score medewerker:</span>
                    <ScoreDisplay score={selfEval.score} size="sm" />
                    {selfEval.toelichting && (
                      <span className="text-sm text-yellow-600 italic">
                        "{selfEval.toelichting}"
                      </span>
                    )}
                  </div>
                )}

                {/* Manager score input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jouw beoordeling
                  </label>
                  <ScoreInput
                    value={localScore?.score ?? null}
                    onChange={(score) => handleScoreChange(task.id, score)}
                    showLabels
                    taskId={task.id}
                  />
                </div>

                {/* Agreement Type Selector (required for score 1-2) */}
                {localScore && localScore.score !== null && (
                  <AgreementTypeSelector
                    selectedTypes={localScore.selectedAgreementTypes}
                    onChange={(types) => handleAgreementTypesChange(task.id, types)}
                    isRequired={isAgreementTypeRequired(localScore.score)}
                    agreements={localScore.agreements}
                    onAgreementsChange={(agreements) => handleAgreementsChange(task.id, agreements)}
                  />
                )}

                {/* Comment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Toelichting (optioneel)
                  </label>
                  <textarea
                    rows={2}
                    value={localScore?.toelichting || ''}
                    onChange={(e) => handleToelichtingChange(task.id, e.target.value)}
                    placeholder="Geef een toelichting op je beoordeling..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={prevStep}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Vorige
        </button>
        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saveTaskScores.isPending}
            className="px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50"
          >
            {saveTaskScores.isPending ? 'Opslaan...' : 'Opslaan'}
          </button>
          <button
            onClick={handleNext}
            disabled={hasInvalidAgreementTypes}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={hasInvalidAgreementTypes ? 'Selecteer afspraaktypes voor alle taken met score 1 of 2' : ''}
          >
            Volgende: Vakcompetenties
          </button>
        </div>
      </div>
    </div>
  );
}
